import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

/* =========================================================
   SKIN
========================================================= */

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";

  return (
    SKIN_THEMES.find((skin) => skin.id === skinId) ||
    SKIN_THEMES[0]
  );
}

/* =========================================================
   AUTH TOKEN
========================================================= */

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

/* =========================================================
   API URL BUILDER
========================================================= */

function api(path) {
  const base = String(API_BASE_URL || "").replace(/\/$/, "");

  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }

  return `${base}${path}`;
}

/* =========================================================
   VALUE HELPERS
========================================================= */

function nullableNumber(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const numberValue = Number(value);

  return Number.isFinite(numberValue)
    ? numberValue
    : null;
}

function normalizeAllocationType(guard) {
  const rawType = String(
    guard?.type ||
      guard?.allocation_type ||
      ""
  )
    .trim()
    .toLowerCase();

  if (
    rawType === "place" ||
    guard?.place_id !== null &&
      guard?.place_id !== undefined
  ) {
    return "place";
  }

  return "route";
}

function getAllocationKey(guard) {
  if (
    guard?.allocation_id !== null &&
    guard?.allocation_id !== undefined
  ) {
    return String(guard.allocation_id);
  }

  if (
    guard?.route_allocation_id !== null &&
    guard?.route_allocation_id !== undefined
  ) {
    return String(guard.route_allocation_id);
  }

  if (
    guard?.allocated_id !== null &&
    guard?.allocated_id !== undefined
  ) {
    return String(guard.allocated_id);
  }

  const referenceId =
    guard?.route_id ||
    guard?.place_id ||
    "unknown";

  return `${guard?.guard_id || "guard"}-${referenceId}`;
}

/* =========================================================
   DATE HELPERS

   Backend currently returns timestamps with Z even though the
   values are being displayed/handled as local allocation times.

   Removing Z avoids an unwanted browser timezone conversion.
========================================================= */

function parseAllocationDate(value) {
  if (!value) {
    return null;
  }

  const rawValue = String(value).trim();
  const localValue = rawValue.endsWith("Z")
    ? rawValue.slice(0, -1)
    : rawValue;

  const date = new Date(localValue);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
}

function parseDateTimeLocal(value) {
  if (!value) {
    return null;
  }

  const date = new Date(String(value));

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
}

function formatLocalDateTime(date) {
  if (!(date instanceof Date)) {
    return "";
  }

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const pad = (value) =>
    String(value).padStart(2, "0");

  return [
    date.getFullYear(),
    "-",
    pad(date.getMonth() + 1),
    "-",
    pad(date.getDate()),
    "T",
    pad(date.getHours()),
    ":",
    pad(date.getMinutes()),
  ].join("");
}

function formatDisplayDateTime(value) {
  if (!value) {
    return "—";
  }

  const date = parseAllocationDate(value);

  if (!date) {
    return "Invalid date";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatGeneratedCheckTime(value) {
  if (!value) {
    return "—";
  }

  const date = parseDateTimeLocal(value);

  if (!date) {
    return "Invalid date";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function getDateTimeInputMin(guard) {
  const startDate = parseAllocationDate(
    guard?.allocated_start_time
  );

  return startDate
    ? formatLocalDateTime(startDate)
    : undefined;
}

function getDateTimeInputMax(guard) {
  const endDate = parseAllocationDate(
    guard?.allocated_end_time
  );

  return endDate
    ? formatLocalDateTime(endDate)
    : undefined;
}

/* =========================================================
   RANDOM CHECK HELPERS
========================================================= */

const MIN_RANDOM_ALLOCATION_MINUTES = 120;
const RANDOM_CHECK_INTERVAL_MINUTES = 120;

function getAllocationDurationMinutes(
  startTime,
  endTime
) {
  const startDate = parseAllocationDate(startTime);
  const endDate = parseAllocationDate(endTime);

  if (
    !startDate ||
    !endDate ||
    startDate >= endDate
  ) {
    return 0;
  }

  return Math.floor(
    (endDate.getTime() - startDate.getTime()) /
      (1000 * 60)
  );
}

function generateRandomTimes(startTime, endTime) {
  const startDate = parseAllocationDate(startTime);
  const endDate = parseAllocationDate(endTime);

  if (
    !startDate ||
    !endDate ||
    startDate >= endDate
  ) {
    return [];
  }

  const durationMinutes =
    getAllocationDurationMinutes(
      startTime,
      endTime
    );

  if (
    durationMinutes <
    MIN_RANDOM_ALLOCATION_MINUTES
  ) {
    return [];
  }

  const generatedTimes = [];
  const slotStart = new Date(startDate);

  while (slotStart < endDate) {
    const slotEnd = new Date(
      Math.min(
        slotStart.getTime() +
          RANDOM_CHECK_INTERVAL_MINUTES *
            60 *
            1000,
        endDate.getTime()
      )
    );

    const availableMinutes = Math.floor(
      (slotEnd.getTime() -
        slotStart.getTime()) /
        (1000 * 60)
    );

    if (availableMinutes > 0) {
      const randomOffsetMinutes = Math.floor(
        Math.random() * availableMinutes
      );

      const randomCheckDate = new Date(
        slotStart.getTime() +
          randomOffsetMinutes * 60 * 1000
      );

      if (
        randomCheckDate >= startDate &&
        randomCheckDate < endDate
      ) {
        generatedTimes.push(
          formatLocalDateTime(randomCheckDate)
        );
      }
    }

    slotStart.setMinutes(
      slotStart.getMinutes() +
        RANDOM_CHECK_INTERVAL_MINUTES
    );
  }

  return generatedTimes;
}

/* =========================================================
   ALLOCATION LABEL HELPERS
========================================================= */

function getAllocationReference(guard) {
  const allocationType =
    normalizeAllocationType(guard);

  if (allocationType === "place") {
    return {
      label: "Place ID",
      value: guard?.place_id ?? "—",
    };
  }

  return {
    label: "Route ID",
    value: guard?.route_id ?? "—",
  };
}

function getAllocationBadgeClasses(type) {
  if (type === "place") {
    return [
      "bg-[rgba(168,85,247,0.12)]",
      "text-[#a855f7]",
      "border-[rgba(168,85,247,0.25)]",
    ].join(" ");
  }

  return [
    "bg-[rgba(59,130,246,0.12)]",
    "text-[#3b82f6]",
    "border-[rgba(59,130,246,0.25)]",
  ].join(" ");
}

/* =========================================================
   MAIN PAGE
========================================================= */

export default function InCampusPage() {
  const navigate = useNavigate();
  const skin = useSkin();

  const inputColorScheme =
    skin.id === "snow" ? "light" : "dark";

  const [guards, setGuards] = useState([]);
  const [checkType, setCheckType] =
    useState("random");

  const [
    selectedAllocationIds,
    setSelectedAllocationIds,
  ] = useState([]);

  const [scheduleTimes, setScheduleTimes] =
    useState({});

  const [generatedChecks, setGeneratedChecks] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [refreshing, setRefreshing] =
    useState(false);

  const [dashboardData, setDashboardData] =
    useState({
      company_name: "",
      licensedFor: "",
      admin_active: 0,
      company_address: "",
      company_phone: "",
      admin_email: "",
      company_logo: "",
      user_map: "",
    });

  const routePage = useMemo(() => {
    return dashboardData.user_map === "google"
      ? "/route-master"
      : "/route-master1";
  }, [dashboardData.user_map]);

  const selectedGuards = useMemo(() => {
    return guards.filter((guard) =>
      selectedAllocationIds.includes(
        getAllocationKey(guard)
      )
    );
  }, [guards, selectedAllocationIds]);

  const allSelected = useMemo(() => {
    return (
      guards.length > 0 &&
      selectedAllocationIds.length ===
        guards.length
    );
  }, [
    guards.length,
    selectedAllocationIds.length,
  ]);

  useEffect(() => {
    const token = getToken();
    const isLoggedIn =
      localStorage.getItem("isLoggedIn");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    fetchPageData();
  }, [navigate]);

  /* =======================================================
     LOAD PAGE
  ======================================================= */

  const fetchPageData = async (
    showRefreshToast = false
  ) => {
    try {
      if (showRefreshToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const token = getToken();

      const requestHeaders = token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {};

      const [dashboardResponse, guardsResponse] =
        await Promise.all([
          fetch(
            api(
              "/api/dashboard/admin/dashboard"
            ),
            {
              headers: requestHeaders,
            }
          ),

          fetch(
            api(
              "/api/incampus/today-route-allocated-guards"
            ),
            {
              headers: requestHeaders,
            }
          ),
        ]);

      if (
        dashboardResponse.status === 401 ||
        guardsResponse.status === 401
      ) {
        localStorage.removeItem("token");
        localStorage.removeItem("authToken");
        localStorage.removeItem("adminToken");
        localStorage.removeItem("isLoggedIn");

        navigate("/");
        return;
      }

      if (dashboardResponse.ok) {
        const dashboardJson =
          await dashboardResponse
            .json()
            .catch(() => ({}));

        const companyDetails =
          dashboardJson?.data
            ?.companyDetails || {};

        setDashboardData((previous) => ({
          ...previous,
          ...companyDetails,
        }));
      }

      const guardsJson =
        await guardsResponse
          .json()
          .catch(() => ({}));

      if (!guardsResponse.ok) {
        throw new Error(
          guardsJson?.message ||
            "Failed to load today's allocated guard list."
        );
      }

      const responseList =
        guardsJson?.data ||
        guardsJson?.records ||
        guardsJson ||
        [];

      const list = Array.isArray(responseList)
        ? responseList.map((guard) => ({
            ...guard,

            allocation_id: nullableNumber(
              guard.allocation_id
            ),

            route_id: nullableNumber(
              guard.route_id
            ),

            place_id: nullableNumber(
              guard.place_id
            ),

            type:
              normalizeAllocationType(guard) ===
              "place"
                ? "place"
                : "route",
          }))
        : [];

      setGuards(list);

      setSelectedAllocationIds(
        (previousSelection) =>
          previousSelection.filter(
            (selectedKey) =>
              list.some(
                (guard) =>
                  getAllocationKey(guard) ===
                  selectedKey
              )
          )
      );

      if (showRefreshToast) {
        toast.success(
          `Loaded ${list.length} allocation(s).`
        );
      }
    } catch (error) {
      console.error(
        "[InCampusPage] Load failed:",
        error
      );

      toast.error(
        error.message ||
          "Failed to load In Campus Check page."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  /* =======================================================
     SELECTION
  ======================================================= */

  const toggleGuard = (allocationKey) => {
    setSelectedAllocationIds((previous) => {
      if (previous.includes(allocationKey)) {
        return previous.filter(
          (key) => key !== allocationKey
        );
      }

      return [...previous, allocationKey];
    });

    setGeneratedChecks([]);
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      deselectAll();
      return;
    }

    selectAll();
  };

  const selectAll = () => {
    const allKeys = guards.map((guard) =>
      getAllocationKey(guard)
    );

    setSelectedAllocationIds(allKeys);
    setGeneratedChecks([]);
  };

  const deselectAll = () => {
    setSelectedAllocationIds([]);
    setScheduleTimes({});
    setGeneratedChecks([]);
  };

  const changeCheckType = (nextType) => {
    setCheckType(nextType);
    setGeneratedChecks([]);
  };

  /* =======================================================
     SCHEDULE TIME
  ======================================================= */

  const updateScheduleTime = (
    allocationKey,
    value
  ) => {
    setScheduleTimes((previous) => ({
      ...previous,
      [allocationKey]: value,
    }));

    setGeneratedChecks([]);
  };

  const validateScheduledTime = (
    guard,
    selectedTime
  ) => {
    const scheduledDate =
      parseDateTimeLocal(selectedTime);

    const startDate = parseAllocationDate(
      guard.allocated_start_time
    );

    const endDate = parseAllocationDate(
      guard.allocated_end_time
    );

    if (!scheduledDate) {
      return {
        valid: false,
        message: `Please select a valid check time for ${guard.guard_name}.`,
      };
    }

    if (
      startDate &&
      scheduledDate < startDate
    ) {
      return {
        valid: false,
        message: `The check time for ${guard.guard_name} cannot be earlier than the allocation start time.`,
      };
    }

    if (
      endDate &&
      scheduledDate > endDate
    ) {
      return {
        valid: false,
        message: `The check time for ${guard.guard_name} cannot be later than the allocation end time.`,
      };
    }

    return {
      valid: true,
      message: "",
    };
  };

  /* =======================================================
     BUILD PAYLOAD
  ======================================================= */

  const createCommonCheckData = (guard) => {
    const allocationType =
      normalizeAllocationType(guard);

    const routeId =
      allocationType === "route"
        ? nullableNumber(guard.route_id)
        : null;

    const placeId =
      allocationType === "place"
        ? nullableNumber(guard.place_id)
        : null;

    return {
      allocation_id: nullableNumber(
        guard.allocation_id
      ),

      guard_id: String(
        guard.guard_id || ""
      ).trim(),

      guard_name: String(
        guard.guard_name ||
          guard.guard_id ||
          ""
      ).trim(),

      guard_phone:
        guard.guard_phone || null,

      route_id: routeId,

      place_id: placeId,

      allocation_type: allocationType,

      type: allocationType,

      route_name: String(
        guard.route_name ||
          (allocationType === "place"
            ? `Place ${placeId || ""}`
            : `Route ${routeId || ""}`)
      ).trim(),

      check_type: checkType,
    };
  };

  /* =======================================================
     GENERATE CHECKS
  ======================================================= */

  const handleGenerateChecks = () => {
    if (!selectedAllocationIds.length) {
      toast.error(
        "Please select at least one allocation."
      );
      return;
    }

    const checks = [];

    for (const guard of selectedGuards) {
      const allocationKey =
        getAllocationKey(guard);

      const commonData =
        createCommonCheckData(guard);

      if (!commonData.allocation_id) {
        toast.error(
          `Allocation ID is missing for ${guard.guard_name}.`
        );
        return;
      }

      if (!commonData.guard_id) {
        toast.error(
          "Guard ID is missing for one of the selected allocations."
        );
        return;
      }

      if (
        commonData.allocation_type ===
          "route" &&
        !commonData.route_id
      ) {
        toast.error(
          `Route ID is missing for ${guard.guard_name}.`
        );
        return;
      }

      if (
        commonData.allocation_type ===
          "place" &&
        !commonData.place_id
      ) {
        toast.error(
          `Place ID is missing for ${guard.guard_name}.`
        );
        return;
      }

      if (checkType === "random") {
        const randomTimes =
          generateRandomTimes(
            guard.allocated_start_time,
            guard.allocated_end_time
          );

        if (!randomTimes.length) {
          const durationMinutes =
            getAllocationDurationMinutes(
              guard.allocated_start_time,
              guard.allocated_end_time
            );

          toast.error(
            durationMinutes <
              MIN_RANDOM_ALLOCATION_MINUTES
              ? `${guard.guard_name}'s allocation must be at least ${MIN_RANDOM_ALLOCATION_MINUTES} minutes for random checks.`
              : `Unable to generate random checks for ${guard.guard_name}.`
          );

          return;
        }

        randomTimes.forEach((time) => {
          checks.push({
            ...commonData,
            check_time: time,
          });
        });
      }

      if (checkType === "schedule") {
        const selectedTime =
          scheduleTimes[allocationKey];

        if (!selectedTime) {
          toast.error(
            `Please select a check time for ${guard.guard_name}.`
          );
          return;
        }

        const validation =
          validateScheduledTime(
            guard,
            selectedTime
          );

        if (!validation.valid) {
          toast.error(validation.message);
          return;
        }

        checks.push({
          ...commonData,
          check_time: selectedTime,
        });
      }
    }

    if (!checks.length) {
      toast.error(
        "No valid check times were generated."
      );
      return;
    }

    console.log(
      "[InCampusPage] Generated payload:"
    );

    console.table(
      checks.map((check) => ({
        allocation_id:
          check.allocation_id,
        guard_id: check.guard_id,
        allocation_type:
          check.allocation_type,
        route_id: check.route_id,
        place_id: check.place_id,
        route_name: check.route_name,
        check_type: check.check_type,
        check_time: check.check_time,
      }))
    );

    setGeneratedChecks(checks);

    toast.success(
      `${checks.length} check time(s) generated.`
    );
  };

  /* =======================================================
     SAVE CHECKS
  ======================================================= */

  const handleSubmit = async () => {
    if (!generatedChecks.length) {
      toast.error(
        "Please generate check times first."
      );
      return;
    }

    try {
      setSaving(true);

      const token = getToken();

      const payload = {
        checks: generatedChecks.map(
          (check) => ({
            allocation_id:
              nullableNumber(
                check.allocation_id
              ),

            guard_id: String(
              check.guard_id || ""
            ).trim(),

            guard_name: String(
              check.guard_name || ""
            ).trim(),

            guard_phone:
              check.guard_phone || null,

            route_id:
              check.allocation_type ===
              "route"
                ? nullableNumber(
                    check.route_id
                  )
                : null,

            place_id:
              check.allocation_type ===
              "place"
                ? nullableNumber(
                    check.place_id
                  )
                : null,

            allocation_type:
              check.allocation_type,

            type:
              check.allocation_type,

            route_name: String(
              check.route_name || ""
            ).trim(),

            check_type:
              check.check_type,

            check_time:
              check.check_time,
          })
        ),
      };

      console.log(
        "[InCampusPage] Saving payload:",
        payload
      );

      console.table(
        payload.checks.map((check) => ({
          allocation_id:
            check.allocation_id,
          guard_id: check.guard_id,
          allocation_type:
            check.allocation_type,
          route_id: check.route_id,
          place_id: check.place_id,
          route_name: check.route_name,
          check_type: check.check_type,
          check_time: check.check_time,
        }))
      );

      const response = await fetch(
        api(
          "/api/incampus/in-campus-checks"
        ),
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            ...(token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {}),
          },

          body: JSON.stringify(payload),
        }
      );

      const responseData =
        await response
          .json()
          .catch(() => ({}));

      console.log(
        "[InCampusPage] Save response:",
        {
          status: response.status,
          ok: response.ok,
          responseData,
        }
      );

      if (response.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem(
          "authToken"
        );
        localStorage.removeItem(
          "adminToken"
        );
        localStorage.removeItem(
          "isLoggedIn"
        );

        navigate("/");
        return;
      }

      if (
        !response.ok ||
        responseData?.success === false
      ) {
        const errorMessage =
          responseData?.message ||
          responseData?.error ||
          responseData?.details ||
          `Failed to save checks. HTTP ${response.status}`;

        throw new Error(errorMessage);
      }

      const insertedCount =
        responseData?.data?.insertedCount ||
        responseData?.insertedCount ||
        generatedChecks.length;

      toast.success(
        `${insertedCount} in-campus check(s) saved successfully.`
      );

      setSelectedAllocationIds([]);
      setScheduleTimes({});
      setGeneratedChecks([]);

      await fetchPageData(false);
    } catch (error) {
      console.error(
        "[InCampusPage] Save failed:",
        error
      );

      toast.error(
        error.message ||
          "Failed to save In Campus checks."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleViewList = () => {
    navigate("/incampus_list");
  };

  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-[3px] border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />

        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading in-campus checks...
        </p>
      </div>
    );
  }

  /* =======================================================
     UI
  ======================================================= */

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="in-campus"
    >
      <Toaster position="top-right" />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        {/* ===============================================
            PAGE HEADER
        =============================================== */}

        <div className="flex items-center justify-between gap-3 flex-wrap mb-[22px]">
          <div>
            <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
              In Campus Check
            </h1>

            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Generate checks for today's
              route and place allocations.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                fetchPageData(true)
              }
              disabled={refreshing}
              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer disabled:opacity-60"
            >
              {refreshing
                ? "Refreshing..."
                : "Refresh"}
            </button>

            <button
              type="button"
              onClick={handleViewList}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer"
              style={{
                background:
                  "var(--btn-bg)",
                color:
                  "var(--btn-text)",
              }}
            >
              View List
            </button>
          </div>
        </div>

        {/* ===============================================
            STATS
        =============================================== */}

        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />

          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold m-0">
                In Campus Checking
              </h2>

              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Route allocations save a
                route ID. Place allocations
                save a place ID.
              </p>
            </div>

            <div className="flex gap-3 flex-wrap items-center">
              <StatCard
                label="Allocations"
                value={guards.length}
                color="blue"
              />

              <StatCard
                label="Selected"
                value={
                  selectedAllocationIds.length
                }
                color="green"
              />

              <StatCard
                label="Generated"
                value={
                  generatedChecks.length
                }
                color="amber"
              />
            </div>
          </div>
        </div>

        {/* ===============================================
            ALLOCATION LIST
        =============================================== */}

        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 mb-[18px]">
          <div className="flex items-center justify-between gap-3.5 flex-wrap mb-4">
            <div>
              <h3 className="text-[18px] font-extrabold m-0">
                Allocation List
              </h3>

              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Select one or more route or
                place allocations.
              </p>
            </div>

            <div className="flex gap-1.5 bg-[var(--surface)] p-1.5 rounded-full">
              <button
                type="button"
                onClick={() =>
                  changeCheckType(
                    "random"
                  )
                }
                className="border-0 px-4 py-2 rounded-full text-[12px] font-extrabold cursor-pointer"
                style={{
                  background:
                    checkType ===
                    "random"
                      ? "var(--btn-bg)"
                      : "transparent",

                  color:
                    checkType ===
                    "random"
                      ? "var(--btn-text)"
                      : "var(--text-muted)",
                }}
              >
                Random
              </button>

              <button
                type="button"
                onClick={() =>
                  changeCheckType(
                    "schedule"
                  )
                }
                className="border-0 px-4 py-2 rounded-full text-[12px] font-extrabold cursor-pointer"
                style={{
                  background:
                    checkType ===
                    "schedule"
                      ? "var(--btn-bg)"
                      : "transparent",

                  color:
                    checkType ===
                    "schedule"
                      ? "var(--btn-text)"
                      : "var(--text-muted)",
                }}
              >
                Schedule
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-[var(--surface-border)]">
            <table className="w-full min-w-[920px] text-[13px] border-collapse">
              <thead>
                <tr
                  style={{
                    background:
                      "var(--btn-bg)",
                  }}
                >
                  <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={
                        toggleSelectAll
                      }
                      aria-label="Select all allocations"
                      className="w-4 h-4 cursor-pointer"
                    />
                  </th>

                  <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                    Guard
                  </th>

                  <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                    Allocation
                  </th>

                  <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                    Type
                  </th>

                  <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                    Start Time
                  </th>

                  <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                    End Time
                  </th>

                  {checkType ===
                    "schedule" && (
                    <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                      Check Time
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {guards.length === 0 ? (
                  <tr>
                    <td
                      colSpan={
                        checkType ===
                        "schedule"
                          ? 7
                          : 6
                      }
                      className="text-center py-10 text-[var(--text-muted)]"
                    >
                      No allocations found
                      for today.
                    </td>
                  </tr>
                ) : (
                  guards.map((guard) => {
                    const allocationKey =
                      getAllocationKey(
                        guard
                      );

                    const isSelected =
                      selectedAllocationIds.includes(
                        allocationKey
                      );

                    const allocationType =
                      normalizeAllocationType(
                        guard
                      );

                    const reference =
                      getAllocationReference(
                        guard
                      );

                    return (
                      <tr
                        key={
                          allocationKey
                        }
                        className="border-b border-[var(--divider)] hover:bg-[var(--surface)]"
                      >
                        <td className="px-4 py-3 align-middle">
                          <input
                            type="checkbox"
                            checked={
                              isSelected
                            }
                            onChange={() =>
                              toggleGuard(
                                allocationKey
                              )
                            }
                            aria-label={`Select allocation ${guard.allocation_id}`}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>

                        <td className="px-4 py-3 align-middle">
                          <div className="font-extrabold">
                            {guard.guard_name ||
                              "Unknown guard"}
                          </div>

                          <div className="text-[12px] text-[var(--text-muted)] mt-1">
                            Guard ID:{" "}
                            {guard.guard_id ||
                              "—"}
                          </div>

                          <div className="text-[12px] text-[var(--text-muted)] mt-0.5">
                            Allocation ID:{" "}
                            {guard.allocation_id ||
                              "—"}
                          </div>
                        </td>

                        <td className="px-4 py-3 align-middle">
                          <div className="font-bold">
                            {guard.route_name ||
                              "Unnamed allocation"}
                          </div>

                          <div className="text-[12px] text-[var(--text-muted)] mt-1">
                            {reference.label}:{" "}
                            {reference.value}
                          </div>

                          <div className="text-[12px] text-[var(--text-muted)] mt-0.5">
                            Shift:{" "}
                            {guard.shift_label ||
                              "—"}
                          </div>
                        </td>

                        <td className="px-4 py-3 align-middle">
                          <span
                            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-extrabold uppercase ${getAllocationBadgeClasses(
                              allocationType
                            )}`}
                          >
                            {
                              allocationType
                            }
                          </span>
                        </td>

                        <td className="px-4 py-3 align-middle whitespace-nowrap">
                          {formatDisplayDateTime(
                            guard.allocated_start_time
                          )}
                        </td>

                        <td className="px-4 py-3 align-middle whitespace-nowrap">
                          {formatDisplayDateTime(
                            guard.allocated_end_time
                          )}
                        </td>

                        {checkType ===
                          "schedule" && (
                          <td className="px-4 py-3 align-middle min-w-[230px]">
                            <input
                              type="datetime-local"
                              value={
                                scheduleTimes[
                                  allocationKey
                                ] || ""
                              }
                              min={getDateTimeInputMin(
                                guard
                              )}
                              max={getDateTimeInputMax(
                                guard
                              )}
                              onChange={(
                                event
                              ) =>
                                updateScheduleTime(
                                  allocationKey,
                                  event.target
                                    .value
                                )
                              }
                              disabled={
                                !isSelected
                              }
                              style={{
                                colorScheme:
                                  inputColorScheme,
                              }}
                              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] px-3.5 py-2.5 disabled:opacity-50"
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={selectAll}
                disabled={
                  guards.length === 0
                }
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer disabled:opacity-50"
              >
                Select All
              </button>

              <button
                type="button"
                onClick={deselectAll}
                disabled={
                  selectedAllocationIds.length ===
                  0
                }
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer disabled:opacity-50"
              >
                Deselect All
              </button>
            </div>

            <button
              type="button"
              onClick={
                handleGenerateChecks
              }
              disabled={
                !selectedAllocationIds.length
              }
              style={{
                background:
                  "var(--btn-bg)",
                color:
                  "var(--btn-text)",
              }}
              className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Generate Check Time
            </button>
          </div>
        </div>

        {/* ===============================================
            GENERATED CHECKS
        =============================================== */}

        {generatedChecks.length > 0 && (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6">
            <div className="flex items-center justify-between gap-3.5 flex-wrap mb-4">
              <div>
                <h3 className="text-[18px] font-extrabold m-0">
                  Generated Checks
                </h3>

                <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                  Review the route and place
                  references before saving.
                </p>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background:
                    "#22c55e",
                  color: "#ffffff",
                }}
              >
                {saving
                  ? "Saving..."
                  : "Save Checks"}
              </button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-[var(--surface-border)]">
              <table className="w-full min-w-[850px] text-[13px] border-collapse">
                <thead>
                  <tr
                    style={{
                      background:
                        "var(--btn-bg)",
                    }}
                  >
                    <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                      Guard
                    </th>

                    <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                      Allocation
                    </th>

                    <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                      Route ID
                    </th>

                    <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                      Place ID
                    </th>

                    <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                      Check Type
                    </th>

                    <th className="px-4 py-3 text-left text-[var(--btn-text)]">
                      Check Time
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {generatedChecks.map(
                    (check, index) => {
                      const rowKey = [
                        check.allocation_id ||
                          check.guard_id,
                        check.check_time,
                        index,
                      ].join("-");

                      return (
                        <tr
                          key={rowKey}
                          className="border-b border-[var(--divider)]"
                        >
                          <td className="px-4 py-3 align-middle">
                            <div className="font-extrabold">
                              {
                                check.guard_name
                              }
                            </div>

                            <div className="text-[12px] text-[var(--text-muted)] mt-1">
                              Guard ID:{" "}
                              {
                                check.guard_id
                              }
                            </div>
                          </td>

                          <td className="px-4 py-3 align-middle">
                            <div className="font-bold">
                              {
                                check.route_name
                              }
                            </div>

                            <div className="text-[12px] text-[var(--text-muted)] mt-1">
                              Allocation ID:{" "}
                              {
                                check.allocation_id
                              }
                            </div>

                            <span
                              className={`inline-flex mt-2 items-center rounded-full border px-2.5 py-1 text-[10px] font-extrabold uppercase ${getAllocationBadgeClasses(
                                check.allocation_type
                              )}`}
                            >
                              {
                                check.allocation_type
                              }
                            </span>
                          </td>

                          <td className="px-4 py-3 align-middle">
                            {check.route_id ??
                              "—"}
                          </td>

                          <td className="px-4 py-3 align-middle">
                            {check.place_id ??
                              "—"}
                          </td>

                          <td className="px-4 py-3 align-middle capitalize">
                            {
                              check.check_type
                            }
                          </td>

                          <td className="px-4 py-3 align-middle whitespace-nowrap">
                            {formatGeneratedCheckTime(
                              check.check_time
                            )}
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] px-4 py-3">
              <p className="m-0 text-[12px] text-[var(--text-muted)]">
                Route allocations send{" "}
                <strong>route_id</strong>{" "}
                and set{" "}
                <strong>place_id</strong>{" "}
                to null. Place allocations
                send{" "}
                <strong>place_id</strong>{" "}
                and set{" "}
                <strong>route_id</strong>{" "}
                to null.
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

/* =========================================================
   STAT CARD
========================================================= */

function StatCard({
  label,
  value,
  color,
}) {
  const colorMap = {
    blue: [
      "bg-[rgba(59,130,246,0.12)]",
      "text-[#3b82f6]",
      "border-[rgba(59,130,246,0.2)]",
    ].join(" "),

    green: [
      "bg-[rgba(16,185,129,0.12)]",
      "text-[#10b981]",
      "border-[rgba(16,185,129,0.2)]",
    ].join(" "),

    amber: [
      "bg-[rgba(245,158,11,0.12)]",
      "text-[#f59e0b]",
      "border-[rgba(245,158,11,0.2)]",
    ].join(" "),
  };

  return (
    <div
      className={`rounded-xl px-3 py-2 border ${
        colorMap[color] ||
        colorMap.blue
      }`}
    >
      <p
        className="text-[9px] font-extrabold uppercase tracking-wider opacity-75 m-0"
        style={{
          fontFamily: FONT_UI,
        }}
      >
        {label}
      </p>

      <p
        className="text-[18px] font-extrabold m-0 mt-0.5"
        style={{
          fontFamily:
            FONT_HEADING,
        }}
      >
        {value}
      </p>
    </div>
  );
}